/**
 * BB Mesh Swap — PDP intelligence for Special Order variants.
 * Detects when a 1"×1" or 2"×6" mesh variant is selected; surfaces a callout
 * pointing to in-stock alternatives (2"×2" or 4"×4") that ship in days.
 * Bootstraps off the existing <script type="application/json" data-product-json>.
 */
(function () {
  'use strict';

  // Cross-product fallback: when the WHOLE product is SO (Corten, Green, Brown ZM, etc.),
  // suggest the equivalent in-stock colour of the same product line.
  // Map: SO product handle → array of { handle, label } alternatives.
  var PRODUCT_ALTERNATIVES = {
    'rust-patina-hog-wire-fence-panels-hog-panels-premade-or-unframed-the-red-rock': [
      { handle: 'hog-wire-fence-panels', label: 'Black Hog Wire Fence Panels' },
      { handle: 'the-tough-panel-silver-hog-wire-fence-panel-gauge-6-silver-hog-wire-fence-framed-or-unframe', label: 'Silver Hog Wire Fence Panels' },
      { handle: 'brown-hog-wire-fence-panel-gauge-6-coffee-brown-hog-panels-premade-or-unframed-the-tough-panel', label: 'Brown Hog Wire Fence Panels' },
    ],
    'rust-patina-hog-wire-deck-railing-panels-premade-or-unframed-the-copperhead': [
      { handle: 'hog-wire-deck-railing', label: 'Black Hog Wire Deck Railing' },
      { handle: 'silver-hog-wire-deck-railing-hog-wire-railing-deck-panels', label: 'Silver Hog Wire Deck Railing' },
      { handle: 'brown-hog-wire-deck-railing-panels-premade-or-unframed-the-acadia-copy', label: 'Brown Hog Wire Deck Railing' },
    ],
    'rust-cattle-panel-fence-cattle-panels-premade-or-unframed-the-aberdeen': [
      { handle: 'black-cattle-panel-fence-cattle-panels-premade-or-unframed-the-angus', label: 'Black Cattle Panel Fence' },
      { handle: 'silver-cattle-panel-fence-cattle-panels-premade-or-unframed-the-holstein', label: 'Silver Cattle Panel Fence' },
      { handle: 'brown-cattle-panel-fence-cattle-panels-premade-or-unframed-the-highland', label: 'Brown Cattle Panel Fence' },
    ],
    'dark-green-hog-wire-deck-railing-panels-premade-or-unframed-the-cypress': [
      { handle: 'hog-wire-deck-railing', label: 'Black Hog Wire Deck Railing' },
      { handle: 'silver-hog-wire-deck-railing-hog-wire-railing-deck-panels', label: 'Silver Hog Wire Deck Railing' },
      { handle: 'brown-hog-wire-deck-railing-panels-premade-or-unframed-the-acadia-copy', label: 'Brown Hog Wire Deck Railing' },
    ],
    'green-hog-wire-fence-panels-hog-panels-premade-or-unframed-the-amazon': [
      { handle: 'hog-wire-fence-panels', label: 'Black Hog Wire Fence Panels' },
      { handle: 'the-tough-panel-silver-hog-wire-fence-panel-gauge-6-silver-hog-wire-fence-framed-or-unframe', label: 'Silver Hog Wire Fence Panels' },
      { handle: 'brown-hog-wire-fence-panel-gauge-6-coffee-brown-hog-panels-premade-or-unframed-the-tough-panel', label: 'Brown Hog Wire Fence Panels' },
    ],
    'green-cattle-panel-fence-cattle-panels-premade-or-unframed-the-galloway': [
      { handle: 'black-cattle-panel-fence-cattle-panels-premade-or-unframed-the-angus', label: 'Black Cattle Panel Fence' },
      { handle: 'silver-cattle-panel-fence-cattle-panels-premade-or-unframed-the-holstein', label: 'Silver Cattle Panel Fence' },
      { handle: 'brown-cattle-panel-fence-cattle-panels-premade-or-unframed-the-highland', label: 'Brown Cattle Panel Fence' },
    ],
    'brown-frame-metal-privacy-fence-kit': [
      { handle: 'black-frame-metal-privacy-fence-kit', label: 'Zero-Maintenance Privacy Fence Kit (Black Frame)' },
    ],
    // Aluminum privacy → ZM privacy kit (matched use case)
    'black-aluminum-privacy-fence-panels-96-wide-x-96-high': [
      { handle: 'black-frame-metal-privacy-fence-kit', label: 'Zero-Maintenance Privacy Fence Kit (Black Frame)' },
      { handle: 'corrugated-metal-fence', label: 'Corrugated Metal Privacy Fence Panels' },
    ],
    'black-aluminum-privacy-fence-panels-69-high': [
      { handle: 'black-frame-metal-privacy-fence-kit', label: 'Zero-Maintenance Privacy Fence Kit (Black Frame)' },
      { handle: 'corrugated-metal-fence', label: 'Corrugated Metal Privacy Fence Panels' },
    ],
    'black-aluminum-privacy-fence-panels-46-high': [
      { handle: 'black-frame-metal-privacy-fence-kit', label: 'Zero-Maintenance Privacy Fence Kit (Black Frame)' },
      { handle: 'corrugated-metal-fence', label: 'Corrugated Metal Privacy Fence Panels' },
    ],
    // Aluminum screen → Black Hog Wire (the semi-private look in stock)
    'black-aluminum-screen-fence-panels-46-high': [
      { handle: 'hog-wire-fence-panels', label: 'Black Hog Wire Fence Panels' },
      { handle: 'black-frame-hog-wire-fence-kit', label: 'Zero-Maintenance Hog Wire Fence Kit (Black Frame)' },
    ],
    'black-aluminum-screen-fence-panels-72-wide-x-69-high': [
      { handle: 'hog-wire-fence-panels', label: 'Black Hog Wire Fence Panels' },
      { handle: 'black-frame-hog-wire-fence-kit', label: 'Zero-Maintenance Hog Wire Fence Kit (Black Frame)' },
    ],
    // Aluminum gates → ZM gates
    'black-aluminum-privacy-gate-40-wide-x-44-5-high': [
      { handle: 'gates-for-full-metal-fence-kit', label: 'Zero-Maintenance Metal Fence Gate (Black)' },
    ],
    'black-aluminum-privacy-gate-40-wide-x-67-5-high': [
      { handle: 'gates-for-full-metal-fence-kit', label: 'Zero-Maintenance Metal Fence Gate (Black)' },
    ],
    'black-aluminum-screen-gates-40-wide-x-44-5-high': [
      { handle: 'gates-for-full-metal-fence-kit', label: 'Zero-Maintenance Metal Fence Gate (Black)' },
    ],
    // Steel board → Corrugated Metal (the in-stock metal fencing options)
    'steel-board-fencing': [
      { handle: 'hog-wire-fence-panels', label: 'Black Hog Wire Fence Panels' },
      { handle: 'black-cattle-panel-fence-cattle-panels-premade-or-unframed-the-angus', label: 'Black Cattle Panel Fence' },
    ],
    // 316 Stainless Steel — suggest Silver / Black / Brown standard hog wire
    '316-stainless-steel-wire-fence-panels': [
      { handle: 'the-tough-panel-silver-hog-wire-fence-panel-gauge-6-silver-hog-wire-fence-framed-or-unframe', label: 'Silver Hog Wire Fence Panels' },
      { handle: 'hog-wire-fence-panels', label: 'Black Hog Wire Fence Panels' },
      { handle: 'brown-hog-wire-fence-panel-gauge-6-coffee-brown-hog-panels-premade-or-unframed-the-tough-panel', label: 'Brown Hog Wire Fence Panels' },
    ],
    '316-stainless-steel-deck-railings': [
      { handle: 'silver-hog-wire-deck-railing-hog-wire-railing-deck-panels', label: 'Silver Hog Wire Deck Railing' },
      { handle: 'hog-wire-deck-railing', label: 'Black Hog Wire Deck Railing' },
      { handle: 'brown-hog-wire-deck-railing-panels-premade-or-unframed-the-acadia-copy', label: 'Brown Hog Wire Deck Railing' },
    ],
    '316-stainless-steel-wire-deck-railing': [
      { handle: 'silver-hog-wire-deck-railing-hog-wire-railing-deck-panels', label: 'Silver Hog Wire Deck Railing' },
      { handle: 'hog-wire-deck-railing', label: 'Black Hog Wire Deck Railing' },
      { handle: 'brown-hog-wire-deck-railing-panels-premade-or-unframed-the-acadia-copy', label: 'Brown Hog Wire Deck Railing' },
    ],
  };

  function isSoMesh(val) {
    // 2026-06-10 SO RETIRED (owner: West-only volume covers stock) — original
    // 1x1/2x6 rules preserved in docs/special-order-retirement-2026-06-10.md
    return false;
  }

  // Per-product variant SO rules (in addition to universal mesh check).
  // Each entry: array of { optionRegex, valueRegex }.
  var VARIANT_SO_RULES = {}; // 2026-06-10 SO RETIRED — originals in docs/special-order-retirement-2026-06-10.md

  // Returns { triggered: bool, optionName: string|null } — variant SO state.
  // selectedOpts is a plain object: { "Color": "Brown", "Width": "5\" x 5\"", ... }
  // Canonical in-stock (non-SO) spec per rule-only SO product — the exact
  // option values to recommend so the customer can skip the 4-6 week wait.
  var VARIANT_SO_CANONICAL = {
    'metal-fence-posts-for-wood-fence': { 'Color': 'Black', 'Width': 'True 4" x 4"' },
  };

  function isVariantSO(productHandle, selectedOpts) {
    // Universal mesh check
    for (var k in selectedOpts) {
      if (Object.prototype.hasOwnProperty.call(selectedOpts, k) && /wire.?opening|mesh/i.test(k) && isSoMesh(selectedOpts[k])) {
        return { triggered: true, optionName: k };
      }
    }
    // Per-product rules
    var rules = VARIANT_SO_RULES[productHandle] || [];
    for (var i = 0; i < rules.length; i++) {
      var r = rules[i];
      for (var key in selectedOpts) {
        if (!Object.prototype.hasOwnProperty.call(selectedOpts, key)) continue;
        if (r.optionRegex.test(key) && r.valueRegex.test(String(selectedOpts[key] || ''))) {
          return { triggered: true, optionName: key };
        }
      }
    }
    return { triggered: false, optionName: null };
  }

  // Build a plain { name: value } map for a product variant from /products/{handle}.js
  function variantOptsMap(product, variant) {
    var optsArr = product.options || [];
    var out = {};
    for (var i = 0; i < optsArr.length; i++) {
      var name = typeof optsArr[i] === 'string' ? optsArr[i] : (optsArr[i].name || '');
      if (name) out[name] = variant['option' + (i + 1)];
    }
    return out;
  }

  // Find non-SO sibling variants on the same product. Match all dimensions
  // EXCEPT the trigger option (which is the one we want to switch).
  function findNonSoSiblings(product, currentOptsMap, triggerOptionName) {
    return (product.variants || []).filter(function (v) {
      if (!v.available && (v.inventory_quantity || 0) <= 0) return false;
      var vMap = variantOptsMap(product, v);
      if (isVariantSO(product.handle, vMap).triggered) return false;
      for (var k in currentOptsMap) {
        if (k === triggerOptionName) continue;
        if (vMap[k] !== currentOptsMap[k]) return false;
      }
      return true;
    });
  }

  // Canonical NON-SO variant for a rule-only SO product: current selection
  // with the VARIANT_SO_CANONICAL option(s) overridden (e.g. Color->Black
  // Black, Width->4" x 4"), every other option (e.g. Length) preserved.
  // Returns the matching variant only if it exists, is available, and is
  // genuinely non-SO.
  function findCanonicalNonSo(product, currentOptsMap) {
    var spec = (typeof VARIANT_SO_CANONICAL !== 'undefined' && VARIANT_SO_CANONICAL) ? VARIANT_SO_CANONICAL[product.handle] : null;
    if (!spec) return null;
    var target = {};
    for (var k in currentOptsMap) { if (Object.prototype.hasOwnProperty.call(currentOptsMap, k)) target[k] = currentOptsMap[k]; }
    for (var sk in spec) { if (Object.prototype.hasOwnProperty.call(spec, sk)) target[sk] = spec[sk]; }
    var match = (product.variants || []).filter(function (v) {
      if (!v.available && (v.inventory_quantity || 0) <= 0) return false;
      var vMap = variantOptsMap(product, v);
      for (var t in target) { if (Object.prototype.hasOwnProperty.call(target, t) && vMap[t] !== target[t]) return false; }
      return true;
    })[0];
    if (!match) return null;
    if (isVariantSO(product.handle, variantOptsMap(product, match)).triggered) return null;
    return match;
  }

  function findWireOpeningSelector() {
    // Theme renders `<select data-option-name="wire opening">` per the live template
    return document.querySelector('select[data-option-name="wire opening"], select[data-option-name="mesh opening"]')
        || Array.prototype.find.call(document.querySelectorAll('select.single-option-selector'), function (s) {
          var label = (s.dataset.optionName || s.className || '').toLowerCase();
          return /wire.?opening|mesh/.test(label);
        });
  }

  function findPanelSizeSelector() {
    return document.querySelector('select[data-option-name="panel size"]')
        || Array.prototype.find.call(document.querySelectorAll('select.single-option-selector'), function (s) {
          var label = (s.dataset.optionName || s.className || '').toLowerCase();
          return /panel\s*size|size/.test(label);
        });
  }

  function findFrameSelector() {
    return document.querySelector('select[data-option-name="frame type"]')
        || Array.prototype.find.call(document.querySelectorAll('select.single-option-selector'), function (s) {
          var label = (s.dataset.optionName || s.className || '').toLowerCase();
          return /frame/.test(label);
        });
  }

  function init() {
    var jsonEl = document.querySelector('script[data-product-json]') || document.querySelector('script[type="application/json"][data-product]');
    if (!jsonEl) return;
    var data; try { data = JSON.parse(jsonEl.textContent); } catch (e) { return; }
    var product = data.product || data;
    if (!product || !product.variants) return;

    var hasMeshOption = (product.options || []).some(function (o) {
      var n = typeof o === 'string' ? o : (o.name || '');
      return /wire.?opening|mesh/i.test(n);
    });
    var productIsSo = (product.tags || []).some(function (t) {
      return String(t).toLowerCase() === 'special-order';
    });
    // Render if EITHER the product has mesh option (variant-level SO possible)
    // OR the whole product is SO-tagged (aluminum/ZM/steel board/rust panels).
    // Rule-only SO products (e.g. metal fence posts: SO by colour/width, no mesh
    // option, no special-order tag) must NOT bail here or their VARIANT_SO_RULES
    // never run (callout never shows; bb:so-state never fires for the estimator).
    var hasVariantRules = !!(typeof VARIANT_SO_RULES !== 'undefined' && VARIANT_SO_RULES && VARIANT_SO_RULES[product.handle]);
    if (!hasMeshOption && !productIsSo && !hasVariantRules) return;

    // Build a lookup of variants by their full option signature
    var variants = product.variants;

    function variantMesh(v) {
      // variant.options is an ordered array matching product.options
      var optsArr = product.options || [];
      for (var i = 0; i < optsArr.length; i++) {
        var n = typeof optsArr[i] === 'string' ? optsArr[i] : (optsArr[i].name || '');
        if (/wire.?opening|mesh/i.test(n)) return (v.options || [])[i];
      }
      return null;
    }
    function variantSize(v) {
      var optsArr = product.options || [];
      for (var i = 0; i < optsArr.length; i++) {
        var n = typeof optsArr[i] === 'string' ? optsArr[i] : (optsArr[i].name || '');
        if (/panel\s*size|^size$/i.test(n)) return (v.options || [])[i];
      }
      return null;
    }
    function variantFrame(v) {
      var optsArr = product.options || [];
      for (var i = 0; i < optsArr.length; i++) {
        var n = typeof optsArr[i] === 'string' ? optsArr[i] : (optsArr[i].name || '');
        if (/frame/i.test(n)) return (v.options || [])[i];
      }
      return null;
    }

    // Build callout element
    var callout = document.createElement('div');
    callout.id = 'bb-mesh-swap';
    callout.style.cssText = 'display:none;margin:14px 0;padding:14px 16px;background:#fff3cd;border-left:3px solid #f0ad4e;border-radius:3px;color:#5a3700;font-size:14px;line-height:1.45;';

    // Position: prefer inserting before a wrapper that CONTAINS the ATC button (CA pattern).
    // If no such wrapper, fall back to inserting before .quantity_block (USA pattern).
    var atc = document.querySelector('button[data-add-to-cart], button[name="add"], [data-add-to-cart]');
    var anchor = null;
    var submitWrap = document.querySelector('.product-form__controls-group--submit');
    if (submitWrap && atc && submitWrap.contains(atc)) {
      anchor = submitWrap;
    } else {
      var qty = document.querySelector('.quantity_block, .product-form__controls-group--quantity');
      if (qty && atc && (qty.compareDocumentPosition(atc) & 4)) {
        anchor = qty;
      } else if (atc) {
        anchor = atc;
      }
    }
    if (anchor && anchor.parentNode) {
      anchor.parentNode.insertBefore(callout, anchor);
    } else {
      var form = document.querySelector('form[action*="/cart/add"]');
      if (form) form.parentNode.insertBefore(callout, form);
    }

    function render() {
      var meshSel = findWireOpeningSelector();
      var sizeSel = findPanelSizeSelector();
      var frameSel = findFrameSelector();
      var curMesh = meshSel ? meshSel.value : null;
      var curSize = sizeSel ? sizeSel.value : null;
      var curFrame = frameSel ? frameSel.value : null;

      // Build a Capitalized-keyed map of currently-selected options for the rules check.
      var selectedOptsMap = {};
      var allSelectors = document.querySelectorAll('select.single-option-selector');
      for (var si = 0; si < allSelectors.length; si++) {
        var rawName = allSelectors[si].dataset.optionName || '';
        if (rawName) {
          var capName = rawName.split(' ').map(function (w) { return w.charAt(0).toUpperCase() + w.slice(1); }).join(' ');
          selectedOptsMap[capName] = allSelectors[si].value;
        }
      }
      var variantSO = isVariantSO(product.handle, selectedOptsMap);
      var curIsSo = isSoMesh(curMesh) || variantSO.triggered;
      var showCallout = productIsSo || curIsSo;
      try { document.dispatchEvent(new CustomEvent('bb:so-state', { detail: { isSO: showCallout } })); } catch (_) {}
      if (!showCallout) { callout.style.display = 'none'; return; }

      // Pull the date range from the BB Shipping Estimator (already on the page).
      // The estimator stores the date range inside the header element. When an SO item
      // is in cart, that date is already shifted out 4–6 weeks; otherwise we estimate
      // by reading the rendered date and noting our 4–6 week offset is on top.
      function getEstimatorDateRange() {
        var headerEl = document.getElementById('bb-ship-header-product')
                    || document.getElementById('bb-ship-header-cart');
        if (!headerEl) return null;
        var strong = headerEl.querySelector('strong');
        return strong ? strong.textContent.trim() : null;
      }

      // Compute SO-adjusted range (production + zone transit) directly — we KNOW
      // this is an SO context because the callout only renders when SO is triggered.
      function computeSODateRange() {
        /* ⛔⛔ ONE OWNER FOR THE DELIVERY WINDOW — ONE LANE 2026-11-01. This held a FIFTH copy of the zone
           ETA card: two hardcoded calendar-day tables keyed on a 47-state zone map that also
           listed Alabama, Florida, New York, Pennsylvania, Ontario, Quebec and Newfoundland —
           regions we do not deliver to. Both the figures and the map were retired; the arithmetic
           kept its shape, so nothing flagged it. It now reads the tables the estimator publishes,
           which the PDP accordion also dates from.
           ⛔ The published window is in BUSINESS days (10-30 = 2-6 wks) and this file does
              CALENDAR arithmetic, so convert x7/5 — the same conversion the accordion's Liquid
              does. Re-basing transit to calendar days without converting shortens every window.
           ⭐ The 4-6 week SPECIAL-ORDER production offset is unchanged: that is a production
              claim, not a freight one. */
        var state = null;
        try { var _g = window.BBGeo && window.BBGeo.get && window.BBGeo.get(); state = _g && (_g.state || _g.region); } catch (e) {}
        if (!state) { try { state = sessionStorage.getItem('bb_geo_state'); } catch (e) {} }
        if (!state) return null;
        state = String(state).trim().toUpperCase();
        /* a paused or never-served region gets NO date, the same rule the accordion and the
           freight bar follow. A blank is honest; an arrival date for an address checkout
           refuses is not. */
        if ((window.BB_PAUSED_STATES || {})[state] || (window.BB_PAUSED_PROVINCES || {})[state]
         || (window.BB_UNSERVED_STATES || {})[state] || (window.BB_UNSERVED_PROVINCES || {})[state]) return null;
        var _z = (window.BB_ZONES || {})[state];
        if (!_z) return null;
        var _bd = (window.BB_ZONE_ETA || {})[_z] || (window.BB_ZONE_QS_ETA || {})[_z] || [10, 30];
        if (!_bd || _bd.length < 2) return null;
        var lo = Math.round(_bd[0] * 7 / 5) + 28, hi = Math.round(_bd[1] * 7 / 5) + 42;
        if (!isFinite(lo) || !isFinite(hi) || hi < lo) return null;
        var earliest = new Date(Date.now() + lo * 86400000);
        var latest = new Date(Date.now() + hi * 86400000);
        var M = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        return M[earliest.getMonth()] + ' ' + earliest.getDate() + ' \u2013 ' + M[latest.getMonth()] + ' ' + latest.getDate();
      }

      // If product is whole-SO (rust panels, aluminum, ZM, steel board, etc.),
      // suggest cross-product alternatives (different colour / different fence type
      // that's in stock).
      if (productIsSo && !curIsSo) {
        callout.style.display = 'block';
        var dateRange = computeSODateRange() || getEstimatorDateRange();
        var dateValueHtml = dateRange
          ? '<strong style="color:#5a3700;">' + dateRange + '</strong>'
          : '<span style="opacity:0.6;">calculating…</span>';
        var dateLine = '<div style="margin-top:6px;font-size:13px;"><span style="font-weight:600;">Estimated delivery to your zone:</span> ' + dateValueHtml + ' &middot; <a href="/pages/new-facilities" style="color:#7a4a00;text-decoration:underline;font-size:12px;">Why so long? &rsaquo;</a></div>';

        var crossAlts = PRODUCT_ALTERNATIVES[product.handle] || [];
        var crossAltsHtml = '';
        if (crossAlts.length) {
          crossAltsHtml = '<div style="margin-top:10px;">' +
            '<div style="font-size:12px;font-weight:600;color:#7a4a00;margin-bottom:6px;text-transform:uppercase;letter-spacing:0.4px;">⚡ Want it faster? Standard in-stock options:</div>' +
            crossAlts.slice(0, 3).map(function (alt) {
              return '<a href="/products/' + alt.handle + '" style="display:inline-block;margin:4px 6px 0 0;padding:7px 13px;background:#fff;border:1.5px solid #f0ad4e;border-radius:3px;color:#7a4a00;text-decoration:none;font-weight:600;font-size:13px;">' + alt.label + ' &rsaquo;</a>';
            }).join('') + '</div>';
        }

        callout.innerHTML =
          '<div><strong>⚠ Special Order</strong> &mdash; this product adds <strong>4&ndash;6 weeks</strong> production.</div>' +
          dateLine +
          crossAltsHtml +
          '<div style="margin-top:10px;font-size:12px;"><a href="/pages/special-order" style="color:#7a4a00;text-decoration:underline;font-weight:600;">Why this is Special Order &raquo;</a></div>';
        return;
      }

      // Find alternative non-SO variants. If SO triggered by color/width (non-mesh rule),
      // use findNonSoSiblings — matches all dimensions EXCEPT the trigger option.
      var alts;
      var canonicalAlt = findCanonicalNonSo(product, selectedOptsMap);
      var canonicalLabel = '';
      if (canonicalAlt) {
        var _spec = VARIANT_SO_CANONICAL[product.handle] || {};
        var _parts = [];
        for (var _sk in _spec) { if (Object.prototype.hasOwnProperty.call(_spec, _sk)) _parts.push(_spec[_sk]); }
        canonicalLabel = _parts.join(' ');
      }
      if (variantSO.triggered && variantSO.optionName) {
        alts = findNonSoSiblings(product, selectedOptsMap, variantSO.optionName);
        // Always lead with the canonical in-stock spec — covers the case where
        // multiple options are SO so a single-option switch can't escape it.
        if (canonicalAlt) {
          alts = [canonicalAlt].concat(alts.filter(function (v) { return v.id !== canonicalAlt.id; }));
        }
      } else {
        // Mesh-level SO — original logic
        alts = variants.filter(function (v) {
          if (!v.available && v.inventory_quantity <= 0 && v.inventory_policy !== 'continue') return false;
          var m = variantMesh(v);
          if (isSoMesh(m)) return false;
          if (curSize && variantSize(v) !== curSize) return false;
          if (curFrame && variantFrame(v) !== curFrame) return false;
          return true;
        });
      }

      // Sort: closest-pattern first.
      // If current is 1"×1" (tightest mesh): prefer 2×2 first, then 4×4.
      // If current is 2"×6" (rectangular): prefer 4×4 first (most-similar visually), then 2×2.
      function meshPriority(meshVal) {
        if (/2\s*["'″”]?\s*[x×]\s*2/i.test(meshVal)) return 1;
        if (/4\s*["'″”]?\s*[x×]\s*4/i.test(meshVal)) return 2;
        return 9;
      }
      var preferFineFirst = /\b1\s*["'″”]?\s*[x×]\s*1\b/i.test(curMesh || '');
      alts.sort(function (a, b) {
        var pa = meshPriority(variantMesh(a));
        var pb = meshPriority(variantMesh(b));
        if (preferFineFirst) return pa - pb;          // 1×1 → 2×2 first
        return pb - pa;                                // 2×6 → 4×4 first
      });

      callout.style.display = 'block';
      var altsHtml = '';
      if (alts.length) {
        // For non-mesh SO triggers (color/width), label the alt by its value on the trigger option.
        var triggerName = variantSO.triggered && variantSO.optionName ? variantSO.optionName : null;
        altsHtml = '<div style="margin-top:10px;">' +
          '<div style="font-size:12px;font-weight:600;color:#7a4a00;margin-bottom:6px;text-transform:uppercase;letter-spacing:0.4px;">⚡ Want it faster? Switch to:</div>' +
          alts.slice(0, 3).map(function (v) {
            var altLabel;
            if (canonicalAlt && v.id === canonicalAlt.id) {
              altLabel = canonicalLabel + ' \u2014 in stock, skip the 4\u20136 week wait';
            } else if (triggerName) {
              var vMap = variantOptsMap(product, v);
              altLabel = vMap[triggerName] + ' ' + triggerName.toLowerCase() + ' → skip the 4–6 week wait';
            } else {
              altLabel = variantMesh(v) + ' mesh → skip the 4–6 week wait';
            }
            return '<a href="?variant=' + v.id + '" style="display:inline-block;margin:4px 6px 0 0;padding:7px 13px;background:#fff;border:1.5px solid #f0ad4e;border-radius:3px;color:#7a4a00;text-decoration:none;font-weight:600;font-size:13px;">' + altLabel + '</a>';
          }).join('') + '</div>';
      }

      var dateRange = computeSODateRange() || getEstimatorDateRange();
      var dateValueHtml = dateRange
        ? '<strong style="color:#5a3700;">' + dateRange + '</strong>'
        : '<span style="opacity:0.6;">calculating…</span>';
      var dateLine = '<div style="margin-top:6px;font-size:13px;"><span style="font-weight:600;">Estimated delivery to your zone:</span> ' + dateValueHtml + ' &middot; <a href="/pages/new-facilities" style="color:#7a4a00;text-decoration:underline;font-size:12px;">Why so long? &rsaquo;</a></div>';

      callout.innerHTML =
        '<div><strong>⚠ Special Order</strong> &mdash; ' + (curMesh || 'this') + ' mesh adds <strong>4&ndash;6 weeks</strong> production.</div>' +
        dateLine +
        altsHtml +
        '<div style="margin-top:10px;font-size:12px;"><a href="/pages/special-order" style="color:#7a4a00;text-decoration:underline;">Why some products are Special Order &raquo;</a></div>';
    }

    // Initial render
    render();

    // Listen for native select changes on every option selector
    var watched = [findWireOpeningSelector(), findPanelSizeSelector(), findFrameSelector()].filter(Boolean);
    watched.forEach(function (sel) { sel.addEventListener('change', render); });

    // Also listen for theme-dispatched variant change event
    var section = document.querySelector('.js-product') || document.querySelector('.product-template');
    if (section) section.addEventListener('on:variant:change', render);

    // Re-render whenever the estimator's date range mutates (cart change, zone change, etc.)
    var estContainer = document.getElementById('bb-ship-est-product');
    if (estContainer && typeof MutationObserver !== 'undefined') {
      var mo = new MutationObserver(function () { render(); });
      mo.observe(estContainer, { childList: true, subtree: true, characterData: true });
    } else if (typeof MutationObserver !== 'undefined') {
      // Estimator not yet on page — observe body for it to appear, then attach
      var bodyMo = new MutationObserver(function () {
        var est = document.getElementById('bb-ship-est-product');
        if (est) {
          new MutationObserver(function () { render(); }).observe(est, { childList: true, subtree: true, characterData: true });
          bodyMo.disconnect();
        }
      });
      bodyMo.observe(document.body, { childList: true, subtree: true });
    }
    // Safety net: retry render every 700ms for first 5 seconds to pick up late estimator updates
    var retries = 0;
    var retryTimer = setInterval(function () {
      retries++;
      render();
      if (retries >= 7) clearInterval(retryTimer);
    }, 700);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
